import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import ReactMarkdown from 'react-markdown';
import { useFormContext } from 'react-hook-form';
import { FiEdit3, FiAlertCircle, FiInfo, FiExternalLink, FiKey, FiFileText, FiLayers, FiTarget, FiPlay, FiSave, FiAward, FiCheckCircle, FiAlertTriangle, FiCalendar, FiClock, FiShield, FiArrowRight, FiLock, FiServer, FiRepeat, FiCheck, FiGlobe, FiDatabase, FiSmartphone, FiCloud, FiSearch, FiFilter, FiChevronLeft, FiChevronRight, FiMinus } from 'react-icons/fi';
import EditorLayout from '../../../layouts/EditorLayout';
import EditorTextInput from '../../../components/editor/inputs/EditorTextInput';
import FormDropdown from '../../../components/forms/inputs/FormDropdown';
import { ProgramType } from '../../../utils/api/endpoints/programs/parsePrograms';
import axios from '../../../utils/api/axios';
import toast from 'react-hot-toast';
import { VaultPreSelector } from '../../../components/vault';
import type { VaultLinkConfig } from '../../../components/vault/VaultPreSelector';
import { linkVaultToProgram } from '../../../utils/api/endpoints/vault';
import useProgram from '../../../utils/hooks/programs/useProgram';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useUserDetails from '../../../utils/hooks/user/useUserDetails';
import useUserCredentials from '../../../utils/hooks/user/useUserCredentials';
import { UserRole } from '../../../utils/api/endpoints/user/credentials';
import { getCompanyById, Company, listCompanyNamesAdmin } from '../../../utils/api/endpoints/companies';

// Helper for class names
const clsx = (...classes: (string | boolean | undefined | null)[]) => classes.filter(Boolean).join(' ');

const STORAGE_KEY = 'ctb_program_v2_draft';

// Error Message Helper
const ErrorMessage = ({ error }: { error?: { message?: string } }) => {
    if (!error) return null;
    return (
        <p className="text-red-500 text-[10px] font-bold mt-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
            {error.message || "This field is required"}
        </p>
    );
};

// --- Type Definitions ---

type WizardStep = 'basics' | 'asset' | 'scope' | 'context' | 'credentials' | 'roe' | 'readiness';

// --- PTaaS Wizard Components ---

const WizardProgress = React.memo(({ currentStep, onStepClick }: { currentStep: WizardStep, onStepClick: (step: WizardStep) => void }) => {
    const { formState: { errors }, watch } = useFormContext();
    const [title, scheduling_type, one_off_start_date, one_off_end_date, asset_id, target_ids, scope, credentials, linked_vault_ids, custom_roe] = watch([
        'title',
        'scheduling_type',
        'one_off_start_date',
        'one_off_end_date',
        'asset_id',
        'target_ids',
        'scope',
        'credentials',
        'linked_vault_ids',
        'custom_roe'
    ]);

    const allValues: any = {
        title,
        scheduling_type,
        one_off_start_date,
        one_off_end_date,
        asset_id,
        target_ids,
        scope,
        credentials,
        linked_vault_ids,
        custom_roe
    };

    const steps: { key: WizardStep; label: string; required?: string[]; optional?: string[] }[] = [
        {
            key: 'basics',
            label: 'Basic Information',
            required: allValues.scheduling_type === 'continuous'
                ? ['title']
                : ['title', 'one_off_start_date', 'one_off_end_date']
        },
        { key: 'asset', label: 'Asset Details', required: ['asset_id'] },
        { key: 'scope', label: 'Scope', required: ['target_ids'] },
        { key: 'context', label: 'Context', optional: ['scope'] },
        { key: 'credentials', label: 'Credentials', optional: ['credentials', 'linked_vault_ids'] },
        { key: 'roe', label: 'ROE', optional: ['custom_roe'] },
        { key: 'readiness', label: 'Review' }
    ];

    const currentIdx = steps.findIndex(s => s.key === currentStep);

    return (
        <div className="bg-[#0A192F] py-4 px-10 border-b border-slate-800">
            <div className="w-full flex items-center justify-between">
                {steps.map((s, idx) => {
                    const hasError = s.required?.some(f => errors[f]);
                    const isAllRequiredFilled = s.required ? s.required.every(f => {
                        const val = allValues[f];
                        if (Array.isArray(val)) return val.length > 0;
                        return val && val.toString().trim().length > 0;
                    }) : true;

                    const hasAnyOptionalData = s.optional?.some(f => {
                        const val = allValues[f];
                        if (Array.isArray(val)) return val.length > 0;
                        return val && val.toString().trim().length > 0;
                    });

                    // A step is "completed" if it's valid and has data (required or optional)
                    const isCompleted = isAllRequiredFilled && (s.required ? true : hasAnyOptionalData);
                    const isOptionalSkipped = !s.required && !hasAnyOptionalData && idx < currentIdx;
                    const isWarning = hasError || (idx < currentIdx && !isAllRequiredFilled);

                    return (
                        <React.Fragment key={s.key}>
                            <div
                                onClick={() => onStepClick(s.key)}
                                className={clsx(
                                    "flex items-center gap-3 transition-all duration-300",
                                    idx <= currentIdx + 1 ? "cursor-pointer hover:opacity-80" : "cursor-not-allowed opacity-40 hover:opacity-50"
                                )}
                            >
                                <div className={clsx(
                                    "w-7 h-7 rounded-full flex items-center justify-center border-2 transition-all shadow-sm",
                                    idx === currentIdx ? (isWarning ? "border-red-500 text-red-500 bg-red-50" : "border-[#A3E635] text-[#A3E635] bg-[#A3E635]/10") :
                                        isWarning ? "bg-red-500 border-red-500 text-white" :
                                            isCompleted ? "bg-[#A3E635] border-[#A3E635] text-[#0A192F]" :
                                                isOptionalSkipped ? "border-slate-500 text-slate-500 bg-slate-800/50" :
                                                    "border-slate-600 text-slate-500"
                                )}>
                                    {isWarning ? (
                                        <FiAlertTriangle className="w-3.5 h-3.5" />
                                    ) : isCompleted ? (
                                        <FiCheck className="w-4 h-4" />
                                    ) : isOptionalSkipped ? (
                                        <div className="w-1.5 h-[1.5px] bg-slate-400" />
                                    ) : (
                                        <span className="text-[10px] font-bold">{idx + 1}</span>
                                    )}
                                </div>
                                <span className={clsx(
                                    "text-[10px] font-bold uppercase tracking-wider transition-colors",
                                    idx === currentIdx ? (isWarning ? "text-red-500" : "text-[#A3E635]") :
                                        isWarning ? "text-red-400" :
                                            isCompleted ? "text-slate-200" : "text-slate-500"
                                )}>{s.label}</span>
                            </div>
                            {idx < steps.length - 1 && (
                                <div className={clsx(
                                    "flex-1 h-[1px] mx-4 min-w-[20px] transition-colors duration-500",
                                    idx < currentIdx ? "bg-[#A3E635]/30" : "bg-slate-700"
                                )} />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
});

const StepBasics = React.memo(({ onNext, onSaveDraft, subscriptionType, isReady }: { onNext: () => void, onSaveDraft?: () => void, subscriptionType?: string | null, isReady?: boolean }) => {
    const { setValue, watch, register, trigger, formState: { errors } } = useFormContext();
    // Watch the form state for scheduling_type, default to 'one_off' if not set
    const schedulingType = watch('scheduling_type') || 'one_off';

    const normalizedSub = subscriptionType?.toLowerCase().replace(/[\s_]+/g, '-');
    const isOneOffLike = normalizedSub === 'one-off' || normalizedSub === 'pilot';

    const handleTypeChange = (type: 'one_off' | 'continuous') => {
        // Prevent change if subscription restricts it
        if (isOneOffLike && type === 'continuous') return;
        if (subscriptionType && !isOneOffLike && type === 'one_off') return;
        
        setValue('scheduling_type', type);
    };

    // Auto-select based on subscription type if not already set or if it conflicts
    useEffect(() => {
        if (isOneOffLike && schedulingType !== 'one_off') {
            setValue('scheduling_type', 'one_off');
        } else if (subscriptionType && !isOneOffLike && schedulingType !== 'continuous') {
            setValue('scheduling_type', 'continuous');
        }
    }, [isOneOffLike, subscriptionType, setValue, schedulingType]);

    return (
        <div className="w-full space-y-10 py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="space-y-1">
                <h1 className="text-2xl font-bold text-[#0A192F]">Program Identity</h1>
                <p className="text-slate-500 text-base">Let's start with the basics. Name your program and define its schedule.</p>
            </div>

            <div className="space-y-6">
                <div className="grid grid-cols-1 gap-6">
                    <div className="space-y-2">
                        <label className="text-base font-bold text-[#0A192F]">Program Title <span className="text-emerald-500">*</span></label>
                        <input
                            {...register('title', { required: "Program Title is required" })}
                            className={clsx(
                                "w-full h-11 rounded-xl border px-5 text-base focus:ring-1 outline-none bg-white transition-all shadow-sm",
                                errors.title ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                            )}
                            placeholder="e.g. Q1 2026 External PTaaS"
                        />
                        <ErrorMessage error={errors.title as any} />
                    </div>

                </div>

                <div className="space-y-5">
                    <div className="flex items-center gap-2">
                        <div className="text-emerald-500">
                            <FiClock className="w-5 h-5" />
                        </div>
                        <h3 className="font-bold text-[#0A192F] text-lg">Scheduling Strategy</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* One-off Engagement Card */}
                        <div
                            onClick={() => handleTypeChange('one_off')}
                            className={clsx(
                                "relative p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 flex flex-col justify-between h-48 group",
                                schedulingType === 'one_off'
                                    ? "bg-[#ECFCCB] border-[#A3E635] shadow-lg"
                                    : "bg-white border-slate-100 hover:border-slate-200 hover:shadow-md",
                                subscriptionType && !isOneOffLike && "opacity-50 cursor-not-allowed grayscale"
                            )}
                        >
                            <div className="flex items-start justify-between">
                                <div className={clsx(
                                    "p-3 rounded-xl transition-colors",
                                    schedulingType === 'one_off' ? "bg-[#0A192F] text-white" : "bg-slate-100 text-slate-400 group-hover:bg-slate-200"
                                )}>
                                    <FiCalendar className="w-6 h-6" />
                                </div>
                                {schedulingType === 'one_off' && (
                                    <div className="w-6 h-6 bg-[#0A192F] rounded-full flex items-center justify-center animate-in zoom-in duration-300">
                                        <FiCheck className="w-3.5 h-3.5 text-white" />
                                    </div>
                                )}
                            </div>

                            <div>
                                <h4 className="text-lg font-black text-[#0A192F] mb-2">One-off Engagement</h4>
                                <p className="text-slate-500 text-xs leading-relaxed font-medium">
                                    A single, time-bound pentest execution. Best for compliance or specific release testing.
                                </p>
                            </div>
                        </div>

                        {/* Continuous / Recurring Card */}
                        <div
                            onClick={() => handleTypeChange('continuous')}
                            className={clsx(
                                "relative p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 flex flex-col justify-between h-48 group",
                                schedulingType === 'continuous'
                                    ? "bg-[#ECFCCB] border-[#A3E635] shadow-lg"
                                    : "bg-white border-slate-100 hover:border-slate-200 hover:shadow-md",
                                isOneOffLike && "opacity-50 cursor-not-allowed grayscale"
                            )}
                        >
                            <div className="flex items-start justify-between">
                                <div className={clsx(
                                    "p-3 rounded-xl transition-colors",
                                    schedulingType === 'continuous' ? "bg-[#0A192F] text-white" : "bg-slate-100 text-slate-400 group-hover:bg-slate-200"
                                )}>
                                    <FiRepeat className="w-6 h-6" />
                                </div>
                                {schedulingType === 'continuous' && (
                                    <div className="w-6 h-6 bg-[#0A192F] rounded-full flex items-center justify-center animate-in zoom-in duration-300">
                                        <FiCheck className="w-3.5 h-3.5 text-white" />
                                    </div>
                                )}
                            </div>

                            <div>
                                <h4 className="text-lg font-black text-[#0A192F] mb-2">Continuous / Recurring</h4>
                                <p className="text-slate-500 text-xs leading-relaxed font-medium">
                                    Ongoing testing or recurring cycles. Define your frequency and manage runs manually.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>



                <div className="pt-6 border-t border-slate-100 space-y-8">
                    {schedulingType === 'one_off' && (
                        <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-xl flex items-start gap-3">
                            <FiInfo className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-emerald-700 font-medium leading-relaxed">
                                Note: All dates provided below are <strong>Expected Dates</strong>. The CTB team will review these during the approval process and may adjust them to ensure platform availability and optimal researcher coverage.
                            </p>
                        </div>
                    )}

                    {schedulingType === 'one_off' && (
                        <div className="grid grid-cols-2 gap-8">
                            <div className="space-y-2">
                                <label className="text-[11px] font-bold text-[#0A192F] uppercase tracking-wider">
                                    Expected Start Date
                                </label>
                                <div className="relative group">
                                    <input
                                        type="date"
                                        {...register('one_off_start_date', { required: "Start date is required" })}
                                        className={clsx(
                                            "w-full h-11 rounded-xl border px-5 text-base focus:ring-1 outline-none bg-white transition-all shadow-sm",
                                            errors['one_off_start_date'] ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                                        )}
                                    />
                                    <ErrorMessage error={errors['one_off_start_date'] as any} />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[11px] font-bold text-[#0A192F] uppercase tracking-wider">
                                    Expected End Date
                                </label>
                                <div className="relative group">
                                    <input
                                        type="date"
                                        {...register('one_off_end_date', { required: "End date is required" })}
                                        className={clsx(
                                            "w-full h-11 rounded-xl border px-5 text-base focus:ring-1 outline-none bg-white transition-all shadow-sm",
                                            errors['one_off_end_date'] ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                                        )}
                                    />
                                    <ErrorMessage error={errors['one_off_end_date'] as any} />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

                <div className="pt-8 flex items-center justify-between border-t border-slate-100">
                    <button onClick={() => { }} className="flex items-center gap-2 text-slate-500 font-bold text-xs hover:text-[#0A192F] transition-all">
                        <FiArrowRight className="rotate-180 w-3.5 h-3.5" />
                        Previous
                    </button>

                    <div className="flex items-center gap-5">
                        <button 
                            onClick={() => onSaveDraft?.()}
                            className="flex items-center gap-2 text-slate-500 font-bold text-xs hover:text-[#0A192F] transition-all"
                        >
                            <FiSave className="w-4 h-4" />
                            {isReady ? 'Save' : 'Save Draft'}
                        </button>
                        <button
                            onClick={async () => {
                                const fieldsToValidate = schedulingType === 'one_off'
                                    ? ['title', 'one_off_start_date', 'one_off_end_date']
                                    : ['title'];

                                const isValid = await trigger(fieldsToValidate as any);
                                if (isValid) onNext();
                            }}
                            className="px-8 py-3 bg-[#0A192F] text-white rounded-xl font-bold text-xs flex items-center gap-2.5 hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl translate-y-0 hover:-translate-y-0.5"
                        >
                            Continue
                            <FiArrowRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            </div>
    );
});

const StepAssetSelection = React.memo(({
    onNext,
    onSelect,
    onBack,
    onSaveDraft,
    programId,
    isReady,
    preloadedAssets,
    onPrefetchTargets,
    companyId
}: {
    onNext: () => void;
    onSelect: (assetId: string) => void;
    onBack: () => void;
    onSaveDraft?: () => void;
    programId: number | null;
    isReady?: boolean;
    preloadedAssets?: any[];
    onPrefetchTargets?: (assetId: string) => void;
    companyId?: number | null;
}) => {
    const { setValue, watch, trigger, formState: { errors }, register } = useFormContext();
    const [assets, setAssets] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState<string | null>(watch('asset_id') || null);
    const [searchTerm, setSearchTerm] = useState("");

    // Register asset_id for validation
    useEffect(() => {
        register('asset_id', { required: "Please select an asset to continue" });
    }, [register]);

    useEffect(() => {
        if (preloadedAssets && preloadedAssets.length > 0) {
            setAssets(preloadedAssets);
            setLoading(false);
            return;
        }
        const fetchAssets = async () => {
            try {
                const response = await axios.get(`/assets?exclude_program_id=${programId || ''}${companyId ? `&company_id=${companyId}` : ''}`);
                const assetsData = Array.isArray(response.data?.assets) ? response.data.assets : (Array.isArray(response.data) ? response.data : []);
                const activeAssets = assetsData.filter((a: any) => a.status !== 'draft');
                setAssets(activeAssets);
            } catch (e) {
                console.error("Failed to fetch assets:", e);
                setAssets([]);
            } finally {
                setLoading(false);
            }
        };
        fetchAssets();
    }, [preloadedAssets, programId, companyId]);

    const filteredAssets = useMemo(() => {
        return assets.filter(a => 
            a.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
            a.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (Array.isArray(a.primary_type) ? a.primary_type.join(' ') : (a.primary_type || '')).toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [assets, searchTerm]);

    const handleSelect = (id: string) => {
        setSelectedId(id);
        setValue('asset_id', id);
        onSelect(id);
    };

    const getAssetIcon = (type: any) => {
        const typeStr = Array.isArray(type) ? type.join(' ') : (type || '');
        const lowerType = String(typeStr).toLowerCase();
        if (lowerType.includes('web')) return <FiGlobe className="w-8 h-8" />;
        if (lowerType.includes('api')) return <FiDatabase className="w-8 h-8" />;
        if (lowerType.includes('mobile')) return <FiSmartphone className="w-8 h-8" />;
        if (lowerType.includes('cloud')) return <FiCloud className="w-8 h-8" />;
        return <FiServer className="w-8 h-8" />;
    };

    return (
        <div className="w-full space-y-10 py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="space-y-4 text-center">
                <h1 className="text-3xl font-black text-[#0A192F]">Select Asset</h1>
                <p className="text-slate-400 text-base font-medium">Choose from your {assets.length} active assets to begin.</p>
                
                <div className="relative max-w-md mx-auto mt-6">
                    <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input 
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search assets by name or type..."
                        className="w-full h-11 pl-11 pr-4 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:ring-1 focus:ring-[#A3E635] focus:border-[#A3E635] outline-none transition-all shadow-sm"
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center p-24">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-6 max-w-3xl mx-auto">
                    {filteredAssets.map(asset => (
                        <div
                            key={asset.id}
                            onClick={() => !asset.is_locked_in_cycle && handleSelect(asset.id.toString())}
                            onMouseEnter={() => !asset.is_locked_in_cycle && onPrefetchTargets?.(asset.id.toString())}
                            className={clsx(
                                "group relative p-5 aspect-square rounded-2xl border-2 transition-all duration-300 flex flex-col items-center justify-center text-center",
                                asset.is_locked_in_cycle 
                                    ? "border-slate-200 bg-slate-50/30 cursor-not-allowed" 
                                    : "cursor-pointer border-slate-100 hover:border-emerald-200 bg-white hover:shadow-lg",
                                !asset.is_locked_in_cycle && selectedId === asset.id.toString() && "border-emerald-500 bg-white ring-4 ring-emerald-50 shadow-xl"
                            )}
                        >
                            {/* Radio indicator */}
                            <div className="absolute top-4 right-4">
                                {asset.is_locked_in_cycle ? (
                                    <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-200 border border-slate-300 rounded-md shadow-sm">
                                        <FiLock className="w-2.5 h-2.5 text-slate-500" />
                                        <span className="text-[8px] font-black text-slate-600 uppercase tracking-tighter">Locked</span>
                                    </div>
                                ) : (
                                    <div className={clsx(
                                        "w-4 h-4 rounded-full border-2 transition-all flex items-center justify-center",
                                        selectedId === asset.id.toString()
                                            ? "bg-emerald-500 border-emerald-500"
                                            : "border-slate-200"
                                    )}>
                                        {selectedId === asset.id.toString() && <FiCheck className="text-white w-2.5 h-2.5" />}
                                    </div>
                                )}
                            </div>

                            {/* Icon Container */}
                            <div className={clsx(
                                "mb-3 p-3 rounded-lg transition-transform duration-300",
                                asset.is_locked_in_cycle 
                                    ? "bg-slate-200 text-slate-400" 
                                    : "bg-[#ECFCCB] text-[#A3E635] group-hover:scale-110"
                            )}>
                                {getAssetIcon(asset.primary_type)}
                            </div>

                            <h3 className={clsx(
                                "text-sm font-black mb-1.5",
                                asset.is_locked_in_cycle ? "text-slate-400" : "text-[#0A192F]"
                            )}>{asset.name}</h3>

                            <div className="mb-2">
                                <span className={clsx(
                                    "text-[8px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full",
                                    asset.is_locked_in_cycle ? "bg-slate-100 text-slate-300" : "bg-slate-100 text-slate-400"
                                )}>
                                    {Array.isArray(asset.primary_type) ? asset.primary_type.join(', ') : (asset.primary_type || 'Web')}
                                </span>
                            </div>

                            <div className={clsx(
                                "mt-2 p-2 rounded-lg text-center",
                                asset.is_locked_in_cycle ? "bg-amber-50 border border-amber-100" : ""
                            )}>
                                <p className={clsx(
                                    "text-[10px] leading-relaxed max-w-[90%] mx-auto font-bold line-clamp-4",
                                    asset.is_locked_in_cycle ? "text-amber-700" : "text-slate-400"
                                )}>
                                    {asset.is_locked_in_cycle 
                                        ? "You can create another comprehensive run under the program in which you have already added this asset." 
                                        : (asset.description || "Main production suite and customer portals.")}
                                </p>
                            </div>
                        </div>
                    ))}

                    {assets.length === 0 && (
                        <div className="col-span-full p-24 bg-slate-50 border-2 border-dashed border-slate-200 rounded-[2.5rem] text-center">
                            <p className="text-slate-400 font-bold text-lg">
                                {companyId ? "No active assets found for this organization." : "No active assets found. Please ensure an organization with assets is selected."}
                            </p>
                        </div>
                    )}
                </div>
            )}

            <div className="flex justify-center pb-4">
                <ErrorMessage error={errors.asset_id as any} />
            </div>

            <div className="pt-10 flex items-center justify-between border-t border-slate-100">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-slate-500 font-bold text-xs hover:text-[#0A192F] transition-all"
                >
                    <FiArrowRight className="rotate-180 w-3.5 h-3.5" />
                    Previous
                </button>

                <div className="flex items-center gap-5">
                    <button 
                        onClick={() => onSaveDraft?.()}
                        className="flex items-center gap-2 text-slate-500 font-bold text-xs hover:text-[#0A192F] transition-all"
                    >
                        <FiSave className="w-4 h-4" />
                        {isReady ? 'Save' : 'Save Draft'}
                    </button>
                    <button
                        onClick={async () => {
                            const isValid = await trigger('asset_id' as any);
                            if (isValid) onNext();
                        }}
                        className={clsx(
                            "px-8 py-3 rounded-xl font-bold text-xs flex items-center gap-2.5 transition-all shadow-lg hover:shadow-xl translate-y-0 hover:-translate-y-0.5",
                            selectedId ? "bg-[#0A192F] text-white hover:bg-slate-800" : "bg-slate-200 text-slate-400 cursor-not-allowed"
                        )}
                    >
                        Continue
                        <FiArrowRight className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
});

const mapRawTargets = (targetsData: any[]) => {
    return (Array.isArray(targetsData) ? targetsData : [])
        .filter((target: any) => target && target.target_id && (target.status === 'active' || target.status === 'IN_SCOPE' || target.status === 'in_scope'))
        .map((target: any) => ({
            id: target.target_id?.toString() || '',
            name: target.target_name || 'Unnamed Target',
            type: target.target_type || 'URL',
            value: target.target_value || 'N/A',
            environment: target.environment || 'Production',
            isCovered: !!target.already_covered
        }));
};

const StepScopeEngineering = React.memo(({
    onNext,
    onBack,
    assetId,
    programId,
    initialProgram,
    onSaveDraft,
    isReady,
    cachedTargets,
    onTargetsLoaded,
    companyId
}: {
    onNext: () => void;
    onBack: () => void;
    assetId: string | null;
    programId: number | null;
    initialProgram?: any;
    onSaveDraft?: () => void;
    isReady?: boolean;
    cachedTargets?: any[];
    onTargetsLoaded?: (targets: any[]) => void;
    companyId?: number | null;
}) => {
    const { setValue, register, getValues, trigger, formState: { errors } } = useFormContext();
    const [targets, setTargets] = useState<any[]>(() => {
        if (!cachedTargets || cachedTargets.length === 0) return [];
        return mapRawTargets(cachedTargets);
    });
    const [loading, setLoading] = useState(() => !cachedTargets || cachedTargets.length === 0);
    const [selectedTargetIds, setSelectedTargetIds] = useState<Set<string>>(() => {
        if (cachedTargets && cachedTargets.length > 0) {
            const mapped = mapRawTargets(cachedTargets);
            const sched = getValues('scheduling_type');
            if (sched === 'continuous') {
                return new Set(mapped.map((t: any) => t.id));
            }
        }
        return new Set();
    });
    const [conflictingTargetIds, setConflictingTargetIds] = useState<Set<string>>(new Set());
    
    // Selection Tools State
    const [searchTerm, setSearchTerm] = useState("");
    const [activeFilter, setActiveFilter] = useState<'all' | 'web' | 'api' | 'mobile' | 'infra' | 'cloud' | 'custom'>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 20;

    // Register target_ids for validation
    useEffect(() => {
        register('target_ids', {
            validate: (value) => (Array.isArray(value) && value.length > 0) || "At least one target must be selected"
        });
    }, [register]);

    useEffect(() => {
        // If cachedTargets is available, use it immediately and skip network fetch
        if (cachedTargets && cachedTargets.length > 0) {
            const mappedTargets = mapRawTargets(cachedTargets);
            setTargets(mappedTargets);
            setLoading(false);

            const schedulingType = getValues('scheduling_type');
            const existingIds = getValues('target_ids');

            if (schedulingType === 'continuous') {
                const allIds = new Set(mappedTargets.map((t: any) => t.id));
                setSelectedTargetIds(allIds);
                setValue('target_ids', Array.from(allIds));
            } else if (Array.isArray(existingIds) && existingIds.length > 0) {
                const validExistingIds = existingIds.filter(id => mappedTargets.some(t => String(t.id) === String(id)));
                setSelectedTargetIds(new Set(validExistingIds.map(String)));
            } else if (programId && initialProgram?.target_ids) {
                setSelectedTargetIds(new Set(initialProgram.target_ids.map(String)));
            } else {
                setSelectedTargetIds(new Set(mappedTargets.filter((t: any) => !t.isCovered).map((t: any) => t.id)));
            }
            return;
        }

        const fetchTargets = async () => {
            if (!assetId) {
                setLoading(false);
                return;
            }
            try {
                const schedulingType = getValues('scheduling_type');
                // For continuous programs, all targets of the asset are automatically included.
                // Skipping cycle occupancy and snapshot lock checks makes syncing instant (~20ms).
                const checkOccupancy = schedulingType === 'one_off';
                const response = await axios.get(
                    `/assets/${assetId}/targets?check_cycle_occupancy=${checkOccupancy}&check_linked=false&exclude_program_id=${programId || ''}${companyId ? `&company_id=${companyId}` : ''}`
                );
                const targetsData = Array.isArray(response.data) ? response.data : [];
                const mappedTargets = mapRawTargets(targetsData);
                setTargets(mappedTargets);
                if (onTargetsLoaded) {
                    onTargetsLoaded(targetsData);
                }

                // Initialize selection
                const existingIds = getValues('target_ids');

                if (schedulingType === 'continuous') {
                    // Req 1: All targets selected by default for Continuous V2 and NO access to deselect
                    const allIds = new Set(mappedTargets.map((t: any) => t.id));
                    setSelectedTargetIds(allIds);
                    setValue('target_ids', Array.from(allIds));
                } else if (Array.isArray(existingIds) && existingIds.length > 0) {
                    const validExistingIds = existingIds.filter(id => mappedTargets.some(t => String(t.id) === String(id)));
                    setSelectedTargetIds(new Set(validExistingIds.map(String)));
                } else if (programId && initialProgram?.target_ids) {
                    setSelectedTargetIds(new Set(initialProgram.target_ids.map(String)));
                } else {
                    // Brand new One-off: select all AVAILABLE by default
                    setSelectedTargetIds(new Set(mappedTargets.filter((t: any) => !t.isCovered).map((t: any) => t.id)));
                }

            } catch (e) {
                console.error("Failed to fetch targets:", e);
                setTargets([]);
            } finally {
                setLoading(false);
            }
        };
        fetchTargets();
    }, [assetId, getValues, programId, cachedTargets, initialProgram?.target_ids, onTargetsLoaded, setValue]);

    useEffect(() => {
        if (!loading) {
            setValue('target_ids', Array.from(selectedTargetIds));
        }
    }, [selectedTargetIds, setValue, loading]);

    // --- Selection Logic ---
    const filteredTargets = useMemo(() => {
        return targets.filter((t: any) => {
            const lowSearch = searchTerm.toLowerCase().trim();
            const matchesSearch = !lowSearch || 
                                 t.name.toLowerCase().includes(lowSearch) || 
                                 t.value.toLowerCase().includes(lowSearch);
            
            const lowType = t.type.toLowerCase();
            const matchesFilter = activeFilter === 'all' || 
                                 (activeFilter === 'web' && (lowType.includes('web') || lowType.includes('url') || lowType.includes('callback'))) ||
                                 (activeFilter === 'api' && lowType.includes('api')) ||
                                 (activeFilter === 'mobile' && (lowType.includes('mobile') || lowType.includes('android') || lowType.includes('ios'))) ||
                                 (activeFilter === 'infra' && (lowType.includes('external ip') || lowType.includes('cidr') || lowType.includes('infrastructure') || lowType.includes('host') || lowType.includes('network'))) ||
                                 (activeFilter === 'cloud' && (lowType.includes('cloud') || lowType.includes('project') || lowType.includes('account'))) ||
                                 (activeFilter === 'custom' && lowType.includes('custom'));
            
            return matchesSearch && matchesFilter;
        });
    }, [targets, searchTerm, activeFilter]);

    const totalPages = Math.ceil(filteredTargets.length / ITEMS_PER_PAGE);
    const paginatedTargets = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredTargets.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredTargets, currentPage]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, activeFilter]);

    const handleSelectAllAvailable = () => {
        const newSet = new Set(selectedTargetIds);
        let selectedCount = 0;
        filteredTargets.forEach((t: any) => {
            if (!conflictingTargetIds.has(t.id) && !t.isCovered) {
                newSet.add(t.id);
                selectedCount++;
            }
        });
        setSelectedTargetIds(newSet);
        toast.success(`Selected ${selectedCount} available items`);
    };

    const handleDeselectAllVisible = () => {
        const newSet = new Set(selectedTargetIds);
        filteredTargets.forEach((t: any) => {
            newSet.delete(t.id);
        });
        setSelectedTargetIds(newSet);
        toast.success(`Deselected all filtered items`);
    };

    useEffect(() => {
        const fetchConflicts = async () => {
            if (targets.length === 0) return;

            try {
                const schedulingType = getValues('scheduling_type');
                // For continuous programs, all targets are automatically included for the asset.
                // Conflicts only apply to time-bound one-off programs with defined start/end dates.
                if (schedulingType === 'continuous') return;

                const startDate = getValues('one_off_start_date');
                const endDate = getValues('one_off_end_date');

                if (!startDate || !endDate) return;

                const response = await axios.post('/v2/programs/check-conflicts', {
                    target_ids: targets.map(t => t.id),
                    expected_start_date: startDate,
                    expected_end_date: endDate,
                    exclude_program_id: programId,
                    scheduling_type: schedulingType,
                    company_id: companyId
                });

                const busyIds = new Set<string>((response.data?.conflicting_target_ids || []).map(String));
                setConflictingTargetIds(busyIds);

                // Remove busy IDs from selection if they were somehow selected (auto-selection cleanup)
                setSelectedTargetIds(prev => {
                    const newSet = new Set(prev);
                    let changed = false;
                    busyIds.forEach(id => {
                        if (newSet.has(id)) {
                            newSet.delete(id);
                            changed = true;
                        }
                    });
                    return changed ? newSet : prev;
                });

            } catch (e) {
                console.error("Failed to fetch target conflicts:", e);
            }
        };

        if (!loading) {
            fetchConflicts();
        }
    }, [targets, loading, getValues, programId]);

    const toggleTarget = (id: string) => {
        const target = targets.find(t => String(t.id) === String(id));
        if (conflictingTargetIds.has(id) || target?.isCovered) return; // Prevent selecting conflicting/covered targets

        const newSet = new Set(selectedTargetIds);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedTargetIds(newSet);
    };

    const schedulingType = getValues('scheduling_type');
    const isContinuous = schedulingType === 'continuous';
    const startDate = isContinuous ? getValues('continuous_start_date') : getValues('one_off_start_date');
    const endDate = isContinuous ? getValues('continuous_end_date') : getValues('one_off_end_date');
    const intervalDays = (startDate && endDate) ? moment(endDate).diff(moment(startDate), 'days') + 1 : 0;

    return (
        <div className="w-full space-y-12 py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="space-y-4 text-center">
                <div className="space-y-2">
                    <h1 className="text-4xl font-black text-[#0A192F] tracking-tight">Scope Engineering</h1>
                    <p className="text-slate-500 text-lg font-medium opacity-80 italic">Select the targets you want to include in this program.</p>
                </div>


            </div>

            <div className="space-y-6">
                {isContinuous ? (
                    <div className="py-12 px-8 bg-emerald-50/50 border-2 border-dashed border-emerald-200 rounded-[2.5rem] text-center animate-in zoom-in-95 duration-500">
                        <div className="w-20 h-20 bg-white rounded-3xl shadow-xl shadow-emerald-100/50 flex items-center justify-center mx-auto mb-6 border border-emerald-100 transition-transform hover:scale-110 duration-300">
                            <FiCheckCircle className="w-10 h-10 text-emerald-500" />
                        </div>
                        <h3 className="text-2xl font-black text-[#0A192F] mb-3">All Targets Automatically Included</h3>
                        <p className="text-slate-500 text-sm font-medium max-w-lg mx-auto leading-relaxed">
                            For Continuous programs, we've streamlined the process. All existing and future targets for this asset are automatically managed. 
                            <span className="block mt-2 font-bold text-emerald-600">You can still select specific target segments when scheduling individual runs.</span>
                        </p>
                        
                        <div className="mt-8 flex justify-center gap-3">
                            <div className="px-4 py-2 bg-white border border-emerald-100 rounded-xl shadow-sm flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                <span className="text-[10px] font-black text-emerald-700 uppercase tracking-widest">{targets.length} Targets Synced</span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Control Bar: Search & Filters */}
                        <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md py-4 border-b border-slate-100 space-y-4">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="relative flex-1 max-w-lg">
                                    <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                                    <input 
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Search targets by name or value..."
                                        className="w-full h-11 pl-11 pr-4 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:ring-1 focus:ring-[#A3E635] focus:border-[#A3E635] outline-none transition-all placeholder:text-slate-400"
                                    />
                                </div>

                                <div className="flex items-center gap-2">
                                    <button 
                                        onClick={handleSelectAllAvailable}
                                        className="flex items-center gap-2 px-4 py-2 bg-[#ECFCCB] text-[#0A192F] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[#D9F99D] transition-all shadow-sm active:scale-95"
                                    >
                                        <FiCheckCircle className="w-3.5 h-3.5" /> Select All Available
                                    </button>
                                    <button 
                                        onClick={handleDeselectAllVisible}
                                        className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-500 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-all shadow-sm active:scale-95"
                                    >
                                        <FiMinus className="w-3.5 h-3.5" /> Deselect Filtered
                                    </button>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 overflow-x-auto pb-1 custom-scrollbar">
                                {[
                                    { id: 'all', label: 'All Targets', icon: <FiLayers /> },
                                    { id: 'web', label: 'Web/API', icon: <FiGlobe /> },
                                    { id: 'mobile', label: 'Mobile', icon: <FiSmartphone /> },
                                    { id: 'infra', label: 'Infra', icon: <FiServer /> },
                                    { id: 'cloud', label: 'Cloud', icon: <FiCloud /> },
                                    { id: 'custom', label: 'Custom', icon: <FiTarget /> }
                                ].map(f => (
                                    <button
                                        key={f.id}
                                        onClick={() => setActiveFilter(f.id as any)}
                                        className={clsx(
                                            "flex items-center gap-2 px-4 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest whitespace-nowrap transition-all border",
                                            activeFilter === f.id 
                                                ? "bg-[#0A192F] text-[#A3E635] border-[#0A192F] shadow-lg" 
                                                : "bg-white text-slate-400 border-slate-200 hover:border-slate-300"
                                        )}
                                    >
                                        {f.icon} {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center justify-between px-2">
                            <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">In-Scope Targets ({filteredTargets.length})</h4>
                            <div className="flex items-center gap-2">
                                <span className="text-[9px] font-black text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-100 uppercase tracking-widest">
                                    {selectedTargetIds.size} selected
                                </span>
                                {searchTerm && (
                                    <button onClick={() => setSearchTerm("")} className="text-[9px] font-bold text-slate-400 hover:text-red-500 underline decoration-dotted underline-offset-4">
                                        Clear Search
                                    </button>
                                )}
                            </div>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-20">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500"></div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {paginatedTargets.map((target: any) => (
                                    <div
                                        key={target.id}
                                        onClick={() => toggleTarget(target.id)}
                                        className={clsx(
                                            "group relative p-6 rounded-2xl border transition-all duration-300 flex items-center justify-between bg-white",
                                            (conflictingTargetIds.has(target.id) || target.isCovered)
                                                ? "opacity-50 grayscale bg-slate-50 border-slate-200 cursor-not-allowed"
                                                : selectedTargetIds.has(target.id)
                                                    ? "border-emerald-500 shadow-lg ring-4 ring-emerald-50 cursor-pointer"
                                                    : "border-slate-100 hover:border-slate-200 cursor-pointer"
                                        )}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={clsx(
                                                "w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all duration-200",
                                                selectedTargetIds.has(target.id) && !target.isCovered
                                                    ? "bg-[#A3E635] border-[#A3E635] shadow-[0_0_15px_rgba(163,230,53,0.3)]"
                                                    : (conflictingTargetIds.has(target.id) || target.isCovered)
                                                        ? "bg-slate-100 border-slate-200"
                                                        : "border-slate-200 bg-white group-hover:border-[#A3E635]/50"
                                            )}>
                                                {selectedTargetIds.has(target.id) && !target.isCovered && (
                                                    <FiCheck className="w-4 h-4 text-[#0A192F]" strokeWidth={4} />
                                                )}
                                            </div>
                                            <div className="space-y-3">
                                                <div className="flex items-center gap-2.5">
                                                    <h5 className="font-bold text-[#0A192F] text-lg">{target.name}</h5>
                                                    {conflictingTargetIds.has(target.id) && (
                                                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-amber-100 border border-amber-200 rounded-md">
                                                            <FiLock className="w-2.5 h-2.5 text-amber-600" />
                                                            <span className="text-[9px] font-bold text-amber-700 uppercase tracking-tight">Occupied in timeframe</span>
                                                        </div>
                                                    )}
                                                    {target.isCovered && (
                                                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-blue-50 border border-blue-100 rounded-md">
                                                            <FiCheckCircle className="w-2.5 h-2.5 text-blue-500" />
                                                            <span className="text-[9px] font-bold text-blue-600 uppercase tracking-tight">Already in Cycle</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-500 px-3 py-1 rounded">
                                                        {target.type}
                                                    </span>
                                                    <div className="border border-slate-100 rounded px-4 py-1 text-xs text-slate-400 font-medium">
                                                        {target.value}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {(conflictingTargetIds.has(target.id) || target.isCovered) && (
                                            <div className="text-right">
                                                <div className="text-[10px] font-bold text-slate-400">Unavailable</div>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {paginatedTargets.length === 0 && (
                                    <div className="py-20 text-center bg-slate-50 border-2 border-dashed border-slate-200 rounded-3xl animate-in zoom-in-95 duration-300">
                                        <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mx-auto mb-4 border border-slate-100">
                                            <FiSearch className="w-8 h-8 text-slate-300" />
                                        </div>
                                        <h5 className="text-slate-900 font-black mb-1 text-sm uppercase tracking-widest">No matching targets</h5>
                                        <p className="text-slate-400 text-[10px] font-bold max-w-[240px] mx-auto uppercase tracking-wider leading-relaxed">Try adjusting your filters or searching for a different keyword.</p>
                                    </div>
                                )}

                                {/* Pagination Footer */}
                                {totalPages > 1 && (
                                    <div className="flex items-center justify-center gap-4 py-8 border-t border-slate-100 mt-6 animate-in fade-in slide-in-from-top-2 duration-300">
                                        <button
                                            type="button"
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            className="p-3 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900 hover:border-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm active:scale-95"
                                        >
                                            <FiChevronLeft className="w-5 h-5" />
                                        </button>
                                        
                                        <div className="flex items-center gap-2">
                                            {Array.from({ length: totalPages }).map((_, i) => (
                                                <button
                                                    key={i}
                                                    type="button"
                                                    onClick={() => setCurrentPage(i + 1)}
                                                    className={clsx(
                                                        "w-10 h-10 rounded-xl text-[10px] font-black transition-all shadow-sm",
                                                        currentPage === i + 1 
                                                            ? "bg-[#0A192F] text-[#A3E635] shadow-lg scale-110" 
                                                            : "bg-white text-slate-400 border border-slate-100 hover:border-[#A3E635]/30"
                                                    )}
                                                >
                                                    {i + 1}
                                                </button>
                                            ))}
                                        </div>

                                        <button
                                            type="button"
                                            disabled={currentPage === totalPages}
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            className="p-3 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900 hover:border-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm active:scale-95"
                                        >
                                            <FiChevronRight className="w-5 h-5" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                <div className="flex justify-center">
                    <ErrorMessage error={errors.target_ids as any} />
                </div>
            </div>

            <div className="space-y-6 bg-white rounded-3xl border border-slate-100 p-8 shadow-sm">
                <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl border border-amber-100">
                    <FiAlertTriangle className="w-5 h-5 text-amber-500" />
                    <h4 className="text-xs font-bold text-amber-700 uppercase tracking-widest">Out of Scope</h4>
                </div>
                <textarea
                    {...register('outOfScope')}
                    className="w-full h-44 rounded-2xl border border-slate-100 p-6 text-sm focus:ring-1 focus:ring-emerald-500 outline-none bg-slate-50/30 transition-all"
                    placeholder="e.g. /admin/*, staging.api.com"
                />
            </div>

            <div className="pt-8 flex items-center justify-between border-t border-slate-100">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-slate-500 font-bold text-xs hover:text-[#0A192F] transition-all"
                >
                    <FiArrowRight className="rotate-180 w-3.5 h-3.5" />
                    Previous
                </button>

                <div className="flex items-center gap-5">
                    <button 
                        onClick={() => onSaveDraft?.()}
                        className="flex items-center gap-2 text-slate-500 font-bold text-xs hover:text-[#0A192F] transition-all"
                    >
                        <FiSave className="w-4 h-4" />
                        {isReady ? 'Save' : 'Save Draft'}
                    </button>
                    <button
                        onClick={async () => {
                            const isValid = await trigger('target_ids' as any);
                            if (isValid) onNext();
                        }}
                        className={clsx(
                            "px-8 py-3 rounded-xl font-bold text-xs flex items-center gap-2.5 transition-all shadow-lg hover:shadow-xl translate-y-0 hover:-translate-y-0.5",
                            selectedTargetIds.size > 0 ? "bg-[#0A192F] text-white hover:bg-slate-800" : "bg-slate-200 text-slate-400 cursor-not-allowed"
                        )}
                    >
                        Continue
                        <FiArrowRight className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
});

const StepContextFocus = React.memo(({ onNext, onBack, onSaveDraft, isReady }: { onNext: () => void, onBack: () => void, onSaveDraft?: () => void, isReady?: boolean }) => {
    const { register, trigger, formState: { errors } } = useFormContext();
    return (
        <div className="w-full space-y-12 py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="space-y-2 text-center">
                <h1 className="text-3xl font-bold text-[#0A192F]">Context & Focus</h1>
                <p className="text-slate-500 text-lg font-medium opacity-80">Help researchers understand what matters most.</p>
            </div>

            <div className="space-y-10">
                {/* Upper Section: Business Context & Special Instructions */}
                <div className="space-y-8">
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-[#ECFCCB] text-[#A3E635] rounded-lg">
                                <FiFileText className="w-5 h-5" />
                            </div>
                            <h4 className="text-sm font-bold text-[#0A192F]">Business Context & Special Instructions</h4>
                        </div>
                        
                        <p className="text-xs text-slate-500 font-medium bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-start gap-3">
                            <FiInfo className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                            <span>
                                Combine your business priorities with any specific constraints. 
                                <br /><br />
                                <strong>Example:</strong> "This application handles sensitive PII for EU citizens. Focus on data leakage and authorization bypass. Do not perform intrusive scans between 9 AM - 5 PM UTC."
                            </span>
                        </p>

                        <textarea
                            {...register('scope')}
                            className="w-full h-80 rounded-2xl border border-slate-100 p-6 text-sm focus:ring-1 focus:ring-emerald-500 outline-none bg-white shadow-sm transition-all"
                            placeholder="Describe what to protect and any operational constraints..."
                        />
                        <div className="text-[10px] text-slate-400 font-medium px-1 flex items-center gap-1.5">
                            <FiInfo className="w-3 h-3 text-emerald-500" />
                            Supports Markdown formatting
                        </div>
                    </div>
                </div>
            </div>

            <div className="pt-8 flex items-center justify-between border-t border-slate-100">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-slate-500 font-bold text-xs hover:text-[#0A192F] transition-all"
                >
                    <FiArrowRight className="rotate-180 w-3.5 h-3.5" />
                    Previous
                </button>

                <div className="flex items-center gap-5">
                    <button 
                        onClick={() => onSaveDraft?.()}
                        className="flex items-center gap-2 text-slate-500 font-bold text-xs hover:text-[#0A192F] transition-all"
                    >
                        <FiSave className="w-4 h-4" />
                        {isReady ? 'Save' : 'Save Draft'}
                    </button>
                    <button
                        onClick={async () => {
                            // No explicitly required fields in Step 4 yet, but we trigger to be safe
                            const isValid = await trigger();
                            if (isValid) onNext();
                        }}
                        className="px-8 py-3 bg-[#0A192F] text-white rounded-xl font-bold text-xs flex items-center gap-2.5 hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl translate-y-0 hover:-translate-y-0.5"
                    >
                        Continue
                        <FiArrowRight className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
});

const StepCredentials = React.memo(({ onNext, onBack, vaultConfigs, onVaultConfigChange, onSaveDraft, isReady }: {
    onNext: () => void,
    onBack: () => void,
    vaultConfigs: VaultLinkConfig[],
    onVaultConfigChange: (configs: VaultLinkConfig[]) => void,
    onSaveDraft?: () => void,
    isReady?: boolean
}) => {
    const { setValue, watch, register, trigger, formState: { errors } } = useFormContext();
    const linkedVaultIds = watch('linked_vault_ids') || [];

    // Register credentials (Access Instructions) as optional
    useEffect(() => {
        register('credentials');
    }, [register]);

    return (
        <div className="w-full space-y-12 py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="space-y-2 text-center">
                <h1 className="text-3xl font-bold text-[#0A192F]">Access & Credentials</h1>
                <p className="text-slate-500 text-lg font-medium opacity-80">Manage how researchers access your environment.</p>
            </div>

            <div className="space-y-10">
                <div className="bg-white rounded-[2rem] border border-slate-100 p-8 shadow-sm space-y-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-emerald-50 text-emerald-500 rounded-2xl">
                                <FiKey className="w-6 h-6" />
                            </div>
                            <div className="space-y-0.5">
                                <h4 className="text-lg font-bold text-[#0A192F]">Credential Vault</h4>
                                <p className="text-xs text-slate-400 font-medium">Select existing credentials or create new ones.</p>
                            </div>
                        </div>
                        <a href="/dashboard/vault" target="_blank" className="flex items-center gap-2 px-4 py-2 bg-slate-50 hover:bg-slate-100 text-[11px] font-bold text-slate-600 rounded-xl transition-all uppercase tracking-widest border border-slate-100">
                            Manage Vault <FiExternalLink className="w-3.5 h-3.5" />
                        </a>
                    </div>

                    <div className="bg-slate-50/50 rounded-2xl p-6 border border-slate-100/50">
                        <VaultPreSelector
                            selectedVaultIds={linkedVaultIds}
                            onSelectionChange={(ids) => setValue('linked_vault_ids', ids)}
                            vaultConfigs={vaultConfigs}
                            onVaultConfigChange={onVaultConfigChange}
                        />
                    </div>
                </div>

                {/* Access Instructions Section */}
                <div className="space-y-4">
                    <h4 className="text-[11px] font-black uppercase tracking-widest text-slate-400">Access Instructions</h4>
                    <div className="relative group">
                        <p className="text-[10px] text-slate-500 font-medium mb-3">
                            Provide details on how to access the staging environment, VPN configurations, or specific account handling (e.g., 'Use the test accounts linked in the vault').
                        </p>
                        <textarea
                            {...register('credentials')}
                            className={clsx(
                                "w-full h-56 rounded-3xl border p-8 text-sm focus:ring-1 outline-none bg-white shadow-sm transition-all",
                                errors.credentials ? "border-red-500 focus:ring-red-500" : "border-slate-100 focus:ring-emerald-500"
                            )}
                            placeholder="e.g. 1. Connect to Client VPN using shared config... 2. Access staging.client.com... 3. Use credentials from vault."
                        />
                        <div className="absolute top-6 right-8 text-slate-200 group-focus-within:text-emerald-500/20 transition-colors pointer-events-none">
                            <FiLock className="w-5 h-5" />
                        </div>
                    </div>
                    <ErrorMessage error={errors.credentials as any} />
                </div>
            </div>

            <div className="pt-8 flex items-center justify-between border-t border-slate-100">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-slate-500 font-bold text-xs hover:text-[#0A192F] transition-all"
                >
                    <FiArrowRight className="rotate-180 w-3.5 h-3.5" />
                    Previous
                </button>

                <div className="flex items-center gap-5">
                    <button 
                        onClick={() => onSaveDraft?.()}
                        className="flex items-center gap-2 text-slate-500 font-bold text-xs hover:text-[#0A192F] transition-all"
                    >
                        <FiSave className="w-4 h-4" />
                        {isReady ? 'Save' : 'Save Draft'}
                    </button>
                    <button
                        onClick={async () => {
                            const isValid = await trigger('credentials' as any);
                            if (isValid) onNext();
                        }}
                        className="px-8 py-3 bg-[#0A192F] text-white rounded-xl font-bold text-xs flex items-center gap-2.5 hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl translate-y-0 hover:-translate-y-0.5"
                    >
                        Continue
                        <FiArrowRight className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
});

const StepRoE = React.memo(({ onNext, onBack, onSaveDraft, isReady }: { onNext: () => void, onBack: () => void, onSaveDraft?: () => void, isReady?: boolean }) => {
    const { register, trigger } = useFormContext();
    return (
        <div className="w-full space-y-12 py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="space-y-2 text-center">
                <h1 className="text-3xl font-bold text-[#0A192F]">Rules of Engagement</h1>
                <p className="text-slate-500 text-lg font-medium opacity-80">Set the boundaries and legal terms for this engagement.</p>
            </div>

            <div className="space-y-10">
                {/* Standard Rules Card */}
                <div className="bg-[#0A192F] rounded-[2rem] p-10 shadow-xl space-y-8 relative overflow-hidden">
                    <div className="flex items-center gap-3 relative z-10">
                        <div className="text-emerald-400">
                            <FiAlertCircle className="w-6 h-6" />
                        </div>
                        <h4 className="text-lg font-bold text-white">Standard Rules</h4>
                    </div>

                    <ul className="space-y-6 relative z-10">
                        {[
                            "Do not attempt to access user data belonging to other customers.",
                            "Report critical vulnerabilities immediately.",
                            "Social engineering is strictly prohibited unless explicitly approved in writing."
                        ].map((rule, i) => (
                            <li key={i} className="flex items-start gap-4 text-slate-300 font-medium">
                                <div className="mt-2 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                                <span>{rule}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Additional Testing Boundaries */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                        <label className="text-sm font-bold text-[#0A192F]">Additional Testing Boundaries</label>
                        <div className="group relative">
                            <FiInfo className="text-slate-400 w-3.5 h-3.5" />
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 p-3 bg-[#0A192F] text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 shadow-xl">
                                Define restricted endpoints or methods. E.g., "No automated scanning on /api/v1/delete-all" or "No testing during Sunday maintenance (00:00-04:00 UTC)".
                            </div>
                        </div>
                    </div>
                    <textarea
                        {...register('custom_roe')}
                        className="w-full h-48 rounded-2xl border border-slate-100 p-6 text-sm focus:ring-1 focus:ring-emerald-500 outline-none bg-white shadow-sm transition-all"
                        placeholder="e.g. Social engineering is strictly limited to... / No brute forcing production login..."
                    />
                </div>
            </div>

            <div className="pt-8 flex items-center justify-between border-t border-slate-100">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-slate-500 font-bold text-xs hover:text-[#0A192F] transition-all"
                >
                    <FiArrowRight className="rotate-180 w-3.5 h-3.5" />
                    Previous
                </button>

                <div className="flex items-center gap-5">
                    <button 
                        onClick={() => onSaveDraft?.()}
                        className="flex items-center gap-2 text-slate-500 font-bold text-xs hover:text-[#0A192F] transition-all"
                    >
                        <FiSave className="w-4 h-4" />
                        {isReady ? 'Save' : 'Save Draft'}
                    </button>
                    <button
                        onClick={async () => {
                            const isValid = await trigger();
                            if (isValid) onNext();
                        }}
                        className="px-8 py-3 bg-[#0A192F] text-white rounded-xl font-bold text-xs flex items-center gap-2.5 hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl translate-y-0 hover:-translate-y-0.5"
                    >
                        Continue
                        <FiArrowRight className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
});

const StepReviewLaunch = React.memo(({
    onBack,
    onSaveDraft,
    programId,
    vaultConfigs,
    onJumpToStep,
    isReady,
    cachedAsset,
    cachedTargets,
    companyId
}: {
    onBack: () => void;
    onSaveDraft: (silent?: boolean, isLaunching?: boolean) => Promise<number | null>;
    programId: number | null;
    vaultConfigs: VaultLinkConfig[];
    onJumpToStep: (step: WizardStep) => void;
    isReady: boolean;
    cachedAsset?: any;
    cachedTargets?: any[];
    companyId?: number | null;
}) => {
    const [launching, setLaunching] = useState(false);
    const { watch } = useFormContext();
    const navigate = useNavigate();

    // Watch all required fields
    const title = watch('title');
    const description = watch('description');
    const schedulingType = watch('scheduling_type') || 'one_off';
    const recurrence = watch('recurrence') || 'monthly';
    const assetId = watch('asset_id');
    const targetIds = watch('target_ids') || [];
    const oneOffStart = watch('one_off_start_date');
    const oneOffEnd = watch('one_off_end_date');
    const continuousStart = watch('continuous_start_date');
    const continuousEnd = watch('continuous_end_date');
    const releaseNotes = watch('release_notes');
    const scope = watch('scope');
    const other = watch('other');
    const customRoe = watch('custom_roe');
    const outOfScope = watch('outOfScope');
    const credentials = watch('credentials');
    const linkedVaultIds = watch('linked_vault_ids') || [];

    const [selectedAsset, setSelectedAsset] = useState<any>(() => cachedAsset || null);
    const [assetTargets, setAssetTargets] = useState<any[]>(() => cachedTargets || []);

    // Sync with memory cache when props update
    useEffect(() => {
        if (cachedAsset && !selectedAsset) setSelectedAsset(cachedAsset);
    }, [cachedAsset, selectedAsset]);

    useEffect(() => {
        if (cachedTargets && cachedTargets.length > 0 && assetTargets.length === 0) {
            setAssetTargets(cachedTargets);
        }
    }, [cachedTargets, assetTargets.length]);

    useEffect(() => {
        if (!assetId) return;

        // If both asset and targets are already available from memory, avoid redundant HTTP calls
        if (cachedAsset && cachedTargets && cachedTargets.length > 0) return;

        const calls: Promise<any>[] = [];
        if (!cachedAsset) {
            calls.push(axios.get(`/assets/${assetId}`).catch(err => { console.error(err); return null; }));
        } else {
            calls.push(Promise.resolve({ data: cachedAsset }));
        }

        if (!cachedTargets || cachedTargets.length === 0) {
            calls.push(
                axios.get(`/assets/${assetId}/targets?check_linked=false&check_cycle_occupancy=false${companyId ? `&company_id=${companyId}` : ''}`).catch(err => { console.error(err); return null; })
            );
        } else {
            calls.push(Promise.resolve({ data: cachedTargets }));
        }

        Promise.all(calls).then(([assetRes, targetsRes]) => {
            if (assetRes?.data) setSelectedAsset(assetRes.data);
            if (targetsRes?.data) {
                const allTargets = Array.isArray(targetsRes.data) ? targetsRes.data : [];
                setAssetTargets(allTargets);
            }
        });
    }, [assetId, cachedAsset, cachedTargets]);

    const missingFields: string[] = [];
    if (!title?.trim()) missingFields.push("Program Title");
    if (!assetId) missingFields.push("Asset Selection");
    if (targetIds.length === 0) missingFields.push("In-Scope Targets");

    if (schedulingType === 'one_off') {
        if (!oneOffStart) missingFields.push("Target Start Date");
        if (!oneOffEnd) missingFields.push("Target End Date");
    }

    // const isReady = missingFields.length === 0; // Removed as it's now supplied via prop

    const handleLaunch = async () => {
        if (!isReady) {
            toast.error("Please fill all required fields before launching.");
            return;
        }
        setLaunching(true);
        try {
            const currentProgramId = await onSaveDraft(true, true);
            if (!currentProgramId) {
                setLaunching(false);
                return;
            }
            toast.success("Program Launched Successfully!");
            localStorage.removeItem(STORAGE_KEY);
            navigate('/dashboard/programs');
        } catch (err) {
            console.error(err);
            toast.error("Failed to launch program.");
        } finally {
            setLaunching(false);
        }
    };

    const SummaryCard = ({ title, icon: Icon, children, onEdit }: { title: string, icon: any, children: React.ReactNode, onEdit: () => void }) => (
        <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm relative overflow-hidden group hover:border-emerald-100 transition-all">
            <div className="flex items-center justify-between p-8 pb-6 border-b border-slate-50">
                <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-emerald-50 text-emerald-500 rounded-xl flex items-center justify-center border border-emerald-100">
                        <Icon className="w-5 h-5" />
                    </div>
                    <h4 className="text-lg font-bold text-[#0A192F]">{title}</h4>
                </div>
                <button onClick={onEdit} className="text-slate-400 hover:text-[#0A192F] transition-colors p-2 hover:bg-slate-50 rounded-xl">
                    <FiEdit3 className="w-5 h-5" />
                </button>
            </div>
            <div className="p-8 pt-6 space-y-8">
                {children}
            </div>
        </div>
    );

    const DataField = ({ label, value, className = "" }: { label: string, value: string | undefined | null | React.ReactNode, className?: string }) => (
        <div className={clsx("space-y-1.5", className)}>
            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{label}</label>
            <div className={clsx("font-bold text-[#0A192F] text-sm leading-relaxed", !value && "text-slate-300 italic font-normal")}>
                {value || "Not set"}
            </div>
        </div>
    );

    // Helper to get selected targets details
    const selectedTargetsList = assetTargets.filter(t => targetIds.includes(t.target_id?.toString()));
    const linkedVaultNames = vaultConfigs
        .filter(v => linkedVaultIds.includes(v.vaultId))
        .map(v => v.vaultName)
        .join(', ');

    return (
        <div className="w-full space-y-12 py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="space-y-2 text-center">
                <h1 className="text-3xl font-bold text-[#0A192F]">Ready to Launch?</h1>
                <p className="text-slate-500 text-lg font-medium opacity-80">Please review all program details before submitting.</p>
            </div>

            <div className="space-y-8">
                {/* 1. Program Identity */}
                <SummaryCard title="Program Identity" icon={FiCheckCircle} onEdit={() => onJumpToStep('basics')}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <DataField label="Program Title" value={title} />
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Strategy</label>
                            <div className="flex">
                                <span className="px-3 py-1 bg-[#ECFCCB] text-[#0A192F] rounded-full text-[10px] font-black uppercase tracking-tighter">
                                    {schedulingType === 'one_off' ? 'One-off Engagement' : 'Continuous / Recurring'}
                                </span>
                            </div>
                        </div>
                        {schedulingType === 'one_off' && (
                          <>
                            <DataField
                                label="Expected Start Date"
                                value={oneOffStart ? moment(oneOffStart).format('MMM D, YYYY') : '-'}
                            />
                            <DataField
                                label="Expected End Date"
                                value={oneOffEnd ? moment(oneOffEnd).format('MMM D, YYYY') : '-'}
                            />
                          </>
                        )}
                    </div>
                </SummaryCard>

                {/* 2. Scope & Asset */}
                <SummaryCard title="Scope & Asset" icon={FiTarget} onEdit={() => onJumpToStep('asset')}>
                    <div className="space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                            <DataField label="Selected Asset" value={selectedAsset?.name || "None selected"} />
                            <div className="space-y-1.5">
                                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Target Count</label>
                                <p className="font-bold text-[#0A192F] text-sm">
                                    <span className="text-lg text-emerald-500">{targetIds.length}</span> targets defined
                                </p>
                            </div>
                        </div>

                        {selectedTargetsList.length > 0 && (
                            <div className="pt-6 border-t border-slate-50 space-y-4">
                                <div className="flex items-center justify-between">
                                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">In-Scope Targets</label>
                                    <span className="text-[9px] font-black text-slate-400 bg-slate-100 px-2 py-0.5 rounded uppercase tracking-widest">
                                        {selectedTargetsList.length} items
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 gap-3 max-h-[320px] overflow-y-auto pr-2 custom-scrollbar focus:outline-none">
                                    {selectedTargetsList.map((target, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-slate-400 border border-slate-100">
                                                    <FiTarget className="w-4 h-4" />
                                                </div>
                                                <div>
                                                    <p className="text-xs font-bold text-[#0A192F]">{target.target_name}</p>
                                                    <p className="text-[10px] text-slate-500">{target.target_value}</p>
                                                </div>
                                            </div>
                                            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 bg-white px-2 py-1 rounded border border-slate-100">
                                                {target.target_type}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {outOfScope && (
                            <div className="pt-6 border-t border-slate-50">
                                <div className="p-4 bg-amber-50/50 rounded-xl border border-amber-100 space-y-2">
                                    <div className="flex items-center gap-2 text-amber-600">
                                        <FiAlertTriangle className="w-4 h-4" />
                                        <label className="text-[11px] font-bold uppercase tracking-widest">Out of Scope</label>
                                    </div>
                                    <p className="text-xs font-medium text-slate-600 leading-relaxed pl-6">
                                        <ReactMarkdown className="prose prose-sm max-w-none">
                                            {outOfScope}
                                        </ReactMarkdown>
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </SummaryCard>

                {/* 3. Context & Rules */}
                <SummaryCard title="Context & Rules" icon={FiShield} onEdit={() => onJumpToStep('context')}>
                    <div className="space-y-10">
                        <DataField
                            label="Business Context & Instructions"
                            value={scope ? (
                                <div className="space-y-4">
                                    <div className="space-y-1">
                                        <ReactMarkdown className="prose prose-sm max-w-none text-[#0A192F] font-medium leading-relaxed">{scope}</ReactMarkdown>
                                    </div>
                                </div>
                            ) : null}
                        />
                        {customRoe && (
                            <div className="pt-2 border-t border-slate-50">
                                <DataField
                                    label="Additional Boundaries (ROE)"
                                    value={
                                        <div className="space-y-1">
                                            <ReactMarkdown className="prose prose-sm max-w-none text-[#0A192F] font-medium leading-relaxed">{customRoe}</ReactMarkdown>
                                        </div>
                                    }
                                />
                            </div>
                        )}
                    </div>
                </SummaryCard>

                {/* 4. Access & Credentials */}
                <SummaryCard title="Access & Credentials" icon={FiKey} onEdit={() => onJumpToStep('credentials')}>
                    <div className="space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                            <DataField
                                label="Linked Credentials"
                                value={linkedVaultNames || "No vault credentials linked"}
                            />
                        </div>
                        <div className="pt-6 border-t border-slate-50">
                            <DataField
                                label="Access Instructions"
                                value={credentials ? (
                                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 text-xs font-mono text-slate-600 whitespace-pre-wrap">
                                        {credentials}
                                    </div>
                                ) : "No specific access instructions provided"}
                            />
                        </div>
                    </div>
                </SummaryCard>
            </div>

            <div className="pt-8 flex items-center justify-between border-t border-slate-100">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-slate-500 font-bold text-xs hover:text-[#0A192F] transition-all"
                >
                    <FiArrowRight className="rotate-180 w-3.5 h-3.5" />
                    Previous
                </button>

                <div className="flex items-center gap-5">
                    <button onClick={() => onSaveDraft()} className="flex items-center gap-2 text-slate-500 font-bold text-xs hover:text-[#0A192F] transition-all">
                        <FiSave className="w-4 h-4" />
                        {isReady ? 'Save' : 'Save Draft'}
                    </button>
                    <button
                        onClick={handleLaunch}
                        disabled={launching || !isReady}
                        className={clsx(
                            "px-10 py-3 rounded-xl font-bold text-xs flex items-center gap-2.5 transition-all shadow-lg hover:shadow-xl translate-y-0 hover:-translate-y-0.5",
                            isReady ? "bg-[#0A192F] text-white hover:bg-slate-800" : "bg-slate-200 text-slate-400 cursor-not-allowed"
                        )}
                    >
                        {launching ? "Launching..." : "Continue"}
                        <FiArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
});

const PTaaSWizardContent = ({
    initialProgram,
    subscriptionType,
    preloadedAssets,
    isAdminRole,
    companyId,
    onCompanyChange,
    adminCompanies
}: {
    initialProgram: any;
    subscriptionType?: string | null;
    preloadedAssets?: any[];
    isAdminRole?: boolean;
    companyId?: number | null;
    onCompanyChange?: (newCompanyId: number) => void;
    adminCompanies?: Array<{ company_id: number; company_name: string }>;
}) => {
    const [step, setStep] = useState<WizardStep>(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                const isSameCompany = !isAdminRole || (parsed.companyId && Number(parsed.companyId) === Number(companyId));
                if (parsed.programId === (initialProgram?.id || null) && isSameCompany) return parsed.step || 'basics';
            } catch (e) { }
        }
        return 'basics';
    });

    const [programId, setProgramId] = useState<number | null>(initialProgram?.id || null);
    const [selectedAsset, setSelectedAsset] = useState<string | null>(() => {
        if (initialProgram?.asset_id) return initialProgram.asset_id;
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                const isSameCompany = !isAdminRole || (parsed.companyId && Number(parsed.companyId) === Number(companyId));
                if (parsed.programId === (initialProgram?.id || null) && isSameCompany && parsed.selectedAsset) {
                    return parsed.selectedAsset;
                }
            } catch (e) { }
        }
        return null;
    });
    const [vaultConfigs, setVaultConfigs] = useState<VaultLinkConfig[]>(() => {
        if (initialProgram?.vault_configs) return initialProgram.vault_configs;
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                const isSameCompany = !isAdminRole || (parsed.companyId && Number(parsed.companyId) === Number(companyId));
                if (parsed.programId === (initialProgram?.id || null) && isSameCompany && Array.isArray(parsed.vaultConfigs)) {
                    return parsed.vaultConfigs;
                }
            } catch (e) { }
        }
        return [];
    });
    const [targetsCache, setTargetsCache] = useState<Record<string, any[]>>({});

    const handlePrefetchTargets = React.useCallback((assetId: string) => {
        if (!assetId) return;
        setTargetsCache(prev => {
            if (prev[assetId]) return prev;
            axios.get(`/assets/${assetId}/targets?check_cycle_occupancy=false&check_linked=false&exclude_program_id=${programId || ''}${companyId ? `&company_id=${companyId}` : ''}`)
                .then(res => {
                    const data = Array.isArray(res.data) ? res.data : [];
                    setTargetsCache(current => ({ ...current, [assetId]: data }));
                })
                .catch(() => {});
            return prev;
        });
    }, [programId, companyId]);

    // Prefetch targets as soon as an asset is selected
    useEffect(() => {
        if (!selectedAsset) return;
        if (targetsCache[selectedAsset]) return;

        handlePrefetchTargets(selectedAsset);
    }, [selectedAsset, handlePrefetchTargets, targetsCache]);

    // Try to recover more state from draft on mount if not provided by initialProgram
    useEffect(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                const currentId = initialProgram?.id || null;
                const isSameCompany = !isAdminRole || (parsed.companyId && Number(parsed.companyId) === Number(companyId));
                if (parsed.programId === currentId && isSameCompany) {
                    if (!selectedAsset && parsed.selectedAsset) setSelectedAsset(parsed.selectedAsset);
                    if (vaultConfigs.length === 0 && parsed.vaultConfigs) setVaultConfigs(parsed.vaultConfigs);
                } else if (isAdminRole && parsed.companyId && Number(parsed.companyId) !== Number(companyId)) {
                    // Different company: discard stale draft from previous company
                    localStorage.removeItem(STORAGE_KEY);
                }
            } catch (e) { }
        }
    }, [initialProgram?.id, companyId, isAdminRole]);

    // Sync state when initialProgram changes (e.g., after fetch)
    useEffect(() => {
        if (initialProgram) {
            if (initialProgram.id) setProgramId(initialProgram.id);
            if (initialProgram.asset_id) setSelectedAsset(initialProgram.asset_id);
            if (initialProgram.vault_configs) setVaultConfigs(initialProgram.vault_configs);
        }
    }, [initialProgram]);

    // Scroll to top on step change
    useEffect(() => {
        const scrollContainer = document.getElementById('dashboard-main-scroll');
        if (scrollContainer) {
            scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [step]);

    const { getValues, watch } = useFormContext();
    const [title, asset_id, target_ids, scheduling_type, one_off_start_date, one_off_end_date] = watch([
        'title',
        'asset_id',
        'target_ids',
        'scheduling_type',
        'one_off_start_date',
        'one_off_end_date'
    ]);

    const isReady = useMemo(() => {
        const missing = [];
        if (!title?.trim()) missing.push("Title");
        if (!asset_id) missing.push("Asset");
        if (!target_ids || target_ids.length === 0) missing.push("Scope");
        if (scheduling_type === 'one_off') {
            if (!one_off_start_date) missing.push("Start Date");
            if (!one_off_end_date) missing.push("End Date");
        }
        return missing.length === 0;
    }, [title, asset_id, target_ids, scheduling_type, one_off_start_date, one_off_end_date]);

    // Auto-save effect (debounced to avoid serializing on every keystroke)
    useEffect(() => {
        const timer = setTimeout(() => {
            const draft = {
                step,
                programId,
                companyId: companyId || null,
                selectedAsset,
                vaultConfigs,
                formData: getValues()
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
        }, 1000);
        return () => clearTimeout(timer);
    }, [step, programId, companyId, selectedAsset, vaultConfigs, title, asset_id, target_ids, scheduling_type, one_off_start_date, one_off_end_date, getValues]);

    const { saveProgram } = useProgram();
    const navigate = useNavigate();

    const handleSaveDraft = React.useCallback(async (silent = false, isLaunching = false): Promise<number | null> => {
        const data = getValues();

        const payload: any = {
            ...data,
            company_id: companyId || undefined,
            type: ProgramType.PTAAS,
            private: 1, // Enforce private PTaaS
            vaultConfigs, // Pass vault configurations with access passwords
            status: isLaunching ? 'active' : (isReady ? 'active' : 'draft'), // If ready and clicked "Save", treat as active/visible
            // Map vault IDs for clarity if needed, but they are already in data.linked_vault_ids
        };

        return new Promise((resolve) => {
            saveProgram(payload, {
                architectureVersion: 2,
                programId: programId || undefined,
                skipNavigation: !isLaunching,
                silentToast: silent,
                onSuccess: (id) => {
                    setProgramId(id);
                    if (!silent) toast.success(isReady ? "Program Saved!" : "Draft Saved!");
                    // Update URL to ensure subsequent saves are treated as updates
                    if (!isLaunching) {
                        navigate(`/dashboard/programs/${id}/edit`, { replace: true });
                    }
                    resolve(id);
                },
                onError: (err) => {
                    // useProgram already shows the specific error toast
                    // if (!silent) toast.error("Failed to save draft");
                    console.error("Save Draft Error:", err);
                    resolve(null);
                }
            });
        });
    }, [getValues, vaultConfigs, isReady, saveProgram, programId, navigate, companyId]);

    // When companyId changes, clear selected asset and targets cache
    const { setValue } = useFormContext();
    const prevCompanyIdRef = React.useRef(companyId);
    useEffect(() => {
        if (prevCompanyIdRef.current !== undefined && prevCompanyIdRef.current !== companyId) {
            setSelectedAsset(null);
            setValue('asset_id', null);
            setValue('target_ids', []);
            setTargetsCache({});
        }
        prevCompanyIdRef.current = companyId;
    }, [companyId, setValue]);

    return (
        <div className="min-h-screen bg-white pb-20">
            <WizardProgress currentStep={step} onStepClick={setStep} />

            {isAdminRole && !programId && (
                <div className="w-full bg-[#071324] text-white px-10 py-3.5 border-b border-slate-800 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#A3E635]/20 text-[#A3E635] flex items-center justify-center font-bold">
                            <FiShield className="w-4 h-4" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-slate-200">Creating Program On Behalf Of Business Organization</p>
                            <p className="text-[11px] text-slate-400">Select the client organization to manage assets and scope for this program</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 min-w-[280px]">
                        <select
                            className="w-full bg-[#0A192F] border border-slate-700 text-white text-xs font-semibold rounded-lg px-3 py-2 outline-none focus:border-[#A3E635] focus:ring-1 focus:ring-[#A3E635] cursor-pointer"
                            value={companyId || ""}
                            onChange={(e) => {
                                const newId = e.target.value ? Number(e.target.value) : null;
                                if (newId && onCompanyChange) {
                                    onCompanyChange(newId);
                                }
                            }}
                        >
                            <option value="">-- Choose Business Organization --</option>
                            {companyId && !adminCompanies?.some((c) => c.company_id === companyId) && (
                                <option value={companyId}>
                                    {sessionStorage.getItem("ctb_admin_active_company_name") || `Organization #${companyId}`} (Selected)
                                </option>
                            )}
                            {adminCompanies?.map((c) => (
                                <option key={c.company_id} value={c.company_id}>
                                    {c.company_name} (ID: {c.company_id})
                                </option>
                            ))}
                        </select>
                        {companyId && (
                            <span className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 bg-[#A3E635]/15 text-[#A3E635] text-[11px] font-bold rounded-md border border-[#A3E635]/30">
                                <FiCheck className="w-3 h-3" /> Selected
                            </span>
                        )}
                    </div>
                </div>
            )}

            <div className="w-full mt-8 px-10">
                {step === 'basics' && <StepBasics onNext={() => setStep('asset')} onSaveDraft={handleSaveDraft} subscriptionType={subscriptionType} isReady={isReady} />}
                {step === 'asset' && (
                    <StepAssetSelection
                        onSelect={setSelectedAsset}
                        onNext={() => setStep('scope')}
                        onBack={() => setStep('basics')}
                        onSaveDraft={handleSaveDraft}
                        programId={programId}
                        isReady={isReady}
                        preloadedAssets={preloadedAssets}
                        onPrefetchTargets={handlePrefetchTargets}
                        companyId={companyId}
                    />
                )}
                {step === 'scope' && (
                    <StepScopeEngineering
                        assetId={selectedAsset}
                        programId={programId}
                        initialProgram={initialProgram}
                        onBack={() => setStep('asset')}
                        onNext={() => setStep('context')}
                        onSaveDraft={handleSaveDraft}
                        isReady={isReady}
                        cachedTargets={selectedAsset ? targetsCache[selectedAsset] : undefined}
                        onTargetsLoaded={(data) => {
                            if (selectedAsset) {
                                setTargetsCache(prev => ({ ...prev, [selectedAsset]: data }));
                            }
                        }}
                        companyId={companyId}
                    />
                )}
                {step === 'context' && <StepContextFocus onBack={() => setStep('scope')} onNext={() => setStep('credentials')} onSaveDraft={handleSaveDraft} isReady={isReady} />}
                {step === 'credentials' && (
                    <StepCredentials
                        onBack={() => setStep('context')}
                        onNext={() => setStep('roe')}
                        vaultConfigs={vaultConfigs}
                        onVaultConfigChange={setVaultConfigs}
                        onSaveDraft={handleSaveDraft}
                        isReady={isReady}
                    />
                )}
                {step === 'roe' && <StepRoE onBack={() => setStep('credentials')} onNext={() => setStep('readiness')} onSaveDraft={handleSaveDraft} isReady={isReady} />}
                {step === 'readiness' && (
                    <StepReviewLaunch
                        onBack={() => setStep('roe')}
                        onSaveDraft={handleSaveDraft}
                        programId={programId}
                        vaultConfigs={vaultConfigs}
                        onJumpToStep={setStep}
                        isReady={isReady}
                        cachedAsset={preloadedAssets?.find((a: any) => String(a.id) === String(selectedAsset)) || (initialProgram?.asset?.id && String(initialProgram.asset.id) === String(selectedAsset) ? initialProgram.asset : undefined)}
                        cachedTargets={selectedAsset ? targetsCache[selectedAsset] : undefined}
                        companyId={companyId}
                    />
                )}
            </div>
        </div>
    );
};

const ProgramEditorV2 = () => {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { program, isLoading } = useProgram(id ? Number(id) : undefined);
    const { role } = useUserCredentials();
    const { company_id: userCompanyId, subscription_type: userSubscriptionType } = useUserDetails();

    const isAdminRole = role
        ? [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER].includes(role)
        : false;

    const queryCompanyId = searchParams.get('company_id') || searchParams.get('companyId');
    const [adminCompanies, setAdminCompanies] = useState<Array<{ company_id: number; company_name: string }>>([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(() => {
        if (queryCompanyId && !isNaN(Number(queryCompanyId))) return Number(queryCompanyId);
        const storedCompanyId = sessionStorage.getItem("ctb_admin_active_company_id");
        if (storedCompanyId && !isNaN(Number(storedCompanyId))) return Number(storedCompanyId);
        return null;
    });

    // Keep selectedCompanyId in sync when query params change or if active company is in session
    useEffect(() => {
        if (queryCompanyId && !isNaN(Number(queryCompanyId))) {
            const num = Number(queryCompanyId);
            if (num !== selectedCompanyId) {
                setSelectedCompanyId(num);
                sessionStorage.setItem("ctb_admin_active_company_id", String(num));
            }
        } else if (!selectedCompanyId) {
            const storedCompanyId = sessionStorage.getItem("ctb_admin_active_company_id");
            if (storedCompanyId && !isNaN(Number(storedCompanyId))) {
                setSelectedCompanyId(Number(storedCompanyId));
            }
        }
    }, [queryCompanyId, selectedCompanyId]);

    // Load admin companies for selector
    useEffect(() => {
        if (isAdminRole) {
            listCompanyNamesAdmin()
                .then((companies) => {
                    setAdminCompanies(companies);
                    setSelectedCompanyId((prev) => {
                        if (prev) return prev;
                        const stored = sessionStorage.getItem("ctb_admin_active_company_id");
                        if (stored && !isNaN(Number(stored))) return Number(stored);
                        if (companies.length === 1) return companies[0].company_id;
                        return null;
                    });
                })
                .catch(() => setAdminCompanies([]));
        }
    }, [isAdminRole]);

    // Sync company with program if editing an existing program
    useEffect(() => {
        if (program?.companyId && !selectedCompanyId) {
            const cid = Number(program.companyId);
            setSelectedCompanyId(cid);
            sessionStorage.setItem("ctb_admin_active_company_id", String(cid));
        }
    }, [program?.companyId, selectedCompanyId]);

    const effectiveCompanyId = isAdminRole
        ? selectedCompanyId || (program?.companyId ? Number(program.companyId) : null)
        : (program?.companyId || userCompanyId);

    // Keep URL search params in sync with active effective company for shareable/bookmarkable links
    useEffect(() => {
        if (isAdminRole && effectiveCompanyId && !queryCompanyId) {
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.set('company_id', String(effectiveCompanyId));
                return next;
            }, { replace: true });
        }
    }, [isAdminRole, effectiveCompanyId, queryCompanyId, setSearchParams]);

    const handleCompanyChange = (newCompanyId: number) => {
        // Discard any existing draft from the previous company so it resets cleanly
        localStorage.removeItem(STORAGE_KEY);
        setSelectedCompanyId(newCompanyId);
        sessionStorage.setItem("ctb_admin_active_company_id", String(newCompanyId));
        const found = adminCompanies.find(c => c.company_id === newCompanyId);
        if (found) {
            sessionStorage.setItem("ctb_admin_active_company_name", found.company_name);
        }
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('company_id', String(newCompanyId));
            return next;
        }, { replace: true });
    };

    const [subscriptionType, setSubscriptionType] = useState<string | null>(null);
    const [preloadedAssets, setPreloadedAssets] = useState<any[]>([]);

    // Sync subscriptionType with user details if available and matching
    useEffect(() => {
        if (!isAdminRole && userSubscriptionType && (!program || program.companyId === userCompanyId)) {
            setSubscriptionType(userSubscriptionType);
        }
    }, [isAdminRole, userSubscriptionType, program?.companyId, userCompanyId]);

    useEffect(() => {
        const fetchSubscription = async () => {
            if (effectiveCompanyId) {
                // If userSubscriptionType is already known for this company and not admin, skip redundant HTTP fetch
                if (!isAdminRole && userSubscriptionType && (!program || program.companyId === userCompanyId)) {
                    return;
                }
                try {
                    const company = await getCompanyById(effectiveCompanyId);
                    if (company) {
                        setSubscriptionType(company.subscription_type || null);
                    }
                } catch (error) {
                    console.error("Failed to fetch company subscription:", error);
                }
            }
        };
        if (!isLoading) {
            fetchSubscription();
        }
    }, [effectiveCompanyId, isLoading, isAdminRole, userSubscriptionType, program?.companyId, userCompanyId]);

    useEffect(() => {
        // If admin and no company selected yet, do not fetch assets yet
        if (isAdminRole && !effectiveCompanyId) {
            setPreloadedAssets([]);
            return;
        }

        const checkAssets = async () => {
            try {
                const queryStr = [
                    effectiveCompanyId ? `company_id=${effectiveCompanyId}` : '',
                    id ? `exclude_program_id=${id}` : ''
                ].filter(Boolean).join('&');
                const response = await axios.get(`/assets${queryStr ? `?${queryStr}` : ''}`);
                const assetsData = Array.isArray(response.data?.assets) ? response.data.assets : (Array.isArray(response.data) ? response.data : []);
                // Filter for active assets (assuming status 'draft' implies not ready)
                const activeAssets = assetsData.filter((a: any) => a.status !== 'draft');
                setPreloadedAssets(activeAssets);

                if (!id && !isAdminRole && activeAssets.length === 0) {
                    toast.error("No active assets found. Please add an asset to your inventory before creating a program.", {
                        id: 'no-assets-error',
                        duration: 5000,
                        icon: <FiAlertTriangle className="w-5 h-5 text-amber-500" />
                    });
                    navigate('/dashboard/assets');
                }
            } catch (error) {
                console.error("Failed to check assets for program creation:", error);
            }
        };
        checkAssets();
    }, [id, effectiveCompanyId, isAdminRole, navigate]);

    const defaultValues = React.useMemo(() => {
        let baseValues: any = {
            scheduling_type: 'one_off',
            recurrence: 'monthly',
            title: '',
            description: '',
            target_ids: [],
            linked_vault_ids: [],
            company_id: effectiveCompanyId || undefined
        };

        if (program) {
            baseValues = {
                ...program,
                target_ids: program.target_ids || [],
                linked_vault_ids: program.linked_vault_ids || [],
                company_id: effectiveCompanyId || undefined
            };
        }

        // Merge with localStorage if applicable
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                const currentId = id ? Number(id) : null;
                const isSameCompany = !isAdminRole || (parsed.companyId && Number(parsed.companyId) === Number(effectiveCompanyId));
                // Only merge if it's the same program and the same company
                if (parsed.programId === currentId && isSameCompany && parsed.formData) {
                    return { ...baseValues, ...parsed.formData, company_id: effectiveCompanyId || undefined };
                } else if (isAdminRole && parsed.companyId && Number(parsed.companyId) !== Number(effectiveCompanyId)) {
                    // Previous draft belonged to another company; clear it to prevent cross-company contamination
                    localStorage.removeItem(STORAGE_KEY);
                }
            } catch (e) {
                console.error("Failed to parse saved draft", e);
            }
        }

        return baseValues;
    }, [program, id, effectiveCompanyId, isAdminRole]);

    if (id && isLoading) {
        return <div className="p-12 text-center text-slate-400">Loading program data...</div>;
    }

    return (
        <EditorLayout
            key={`editor-company-${effectiveCompanyId || 'default'}`}
            defaultValues={defaultValues}
            onSubmit={() => { }}
            actions={[]}
            hideFooter={true}
        >
            <PTaaSWizardContent
                initialProgram={program}
                subscriptionType={subscriptionType}
                preloadedAssets={preloadedAssets}
                isAdminRole={isAdminRole}
                companyId={effectiveCompanyId}
                onCompanyChange={handleCompanyChange}
                adminCompanies={adminCompanies}
            />
        </EditorLayout>
    );
};

export default ProgramEditorV2;
